import React, { useState, useEffect, useCallback, useRef } from 'react';
import Konva from 'konva';
import './TextTab.css';
import fontService from '../../services/fontService';
import { useTranslation } from '../../utils/languageUtils';
import { HuePicker } from 'react-color';

const TextTab = ({ konvaController, canvasContainerRef, configData }) => {
  const { t } = useTranslation();
  // State for tracking selected node
  const [selectedNode, setSelectedNode] = useState(null);
  const [text, setText] = useState('');
  // System fonts only - no default custom fonts
  const systemFonts = [
    { name: 'Arial', displayName: 'Arial' },
    { name: 'Helvetica', displayName: 'Helvetica' },
    { name: 'Times New Roman', displayName: 'Times New Roman' },
    { name: 'Georgia', displayName: 'Georgia' },
    { name: 'Verdana', displayName: 'Verdana' },
    { name: 'Courier New', displayName: 'Courier New' },
    { name: 'system-ui', displayName: 'System UI' }
  ];

  const [fontFamily, setFontFamily] = useState('');
  
  const [fontWeight, setFontWeight] = useState('normal');
  const [fontSize, setFontSize] = useState(32);
  const [color, setColor] = useState('#ed0101');
  // Outline (stroke)
  const [strokeColor, setStrokeColor] = useState('#000000');
  const [strokeWidth, setStrokeWidth] = useState(0);
  // Shadow
  const [shadowColor, setShadowColor] = useState('#000000');
  const [shadowBlur, setShadowBlur] = useState(0);
  const [shadowOffsetX, setShadowOffsetX] = useState(0);
  const [shadowOffsetY, setShadowOffsetY] = useState(0);
  const [shadowOpacity, setShadowOpacity] = useState(0.4);
  // Text-only background (behind text)
  const [bgEnabled, setBgEnabled] = useState(false);
  const [bgFill, setBgFill] = useState('#ffffff');
  const [bgStroke, setBgStroke] = useState('#000000');
  const [bgStrokeWidth, setBgStrokeWidth] = useState(1);
  const [bgPadding, setBgPadding] = useState(8);
  const [bgCornerRadius, setBgCornerRadius] = useState(8);
  // Secondary halo (second ring behind main text, behind first halo)
  const [secHaloEnabled, setSecHaloEnabled] = useState(false);
  const [secHaloColor, setSecHaloColor] = useState('#000000');
  const [secHaloWidth, setSecHaloWidth] = useState(8);
  const [customFonts, setCustomFonts] = useState([]);
  const [loadingFonts, setLoadingFonts] = useState(false);
  const [loadingSpecificFont, setLoadingSpecificFont] = useState(false);
  const [fontError, setFontError] = useState('');
  const [fontMetadata, setFontMetadata] = useState([]);
  const [fontLoadingState, setFontLoadingState] = useState({
    isLoading: false,
    fonts: [],
    error: null,
    allFontsLoaded: false
  });
  
  // Track if we should add text on change
  const shouldAddOnChange = useRef(true);
  const fontLoadingRef = useRef(false);
  const textInputRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => {
    try { return window.innerWidth <= 768; } catch { return false; }
  });
  const [fontMenuOpen, setFontMenuOpen] = useState(false);
  const [fontMenuOpenUp, setFontMenuOpenUp] = useState(false);
  const fontAnchorRef = useRef(null);
  const fontMenuRef = useRef(null);
  // removed customColorInputRef; using visible color sidebar instead

  // Comprehensive mobile keyboard dismissal function
  const dismissMobileKeyboard = () => {
    try {
      // Method 1: Blur the specific text input if it exists
      if (textInputRef.current) {
        textInputRef.current.blur();
      }
      
      // Method 2: Blur any currently active element
      if (document.activeElement && document.activeElement.blur) {
        document.activeElement.blur();
      }
      
      // Method 3: For iOS Safari - create temporary input and focus/blur it
      if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
        const tempInput = document.createElement('input');
        tempInput.style.position = 'absolute';
        tempInput.style.left = '-9999px';
        tempInput.style.opacity = '0';
        document.body.appendChild(tempInput);
        tempInput.focus();
        tempInput.blur();
        document.body.removeChild(tempInput);
      }
      
      // Method 4: Trigger window resize event to force layout recalculation
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 100);
      
      console.log('📱 Mobile keyboard dismissed after adding text');
    } catch (error) {
      console.warn('Error dismissing mobile keyboard:', error);
    }
  };

  const handleFontFamilyPick = async (value) => {
    await handleFontFamilyChange({ target: { value } });
  };

  useEffect(() => {
    const onResize = () => {
      try { setIsMobile(window.innerWidth <= 768); } catch {}
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!fontMenuOpen) return;
    const onDown = (e) => {
      try {
        const a = fontAnchorRef.current;
        const m = fontMenuRef.current;
        if (!a || !m) return;
        if (a.contains(e.target) || m.contains(e.target)) return;
        setFontMenuOpen(false);
      } catch {}
    };
    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('touchstart', onDown, true);
    return () => {
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('touchstart', onDown, true);
    };
  }, [fontMenuOpen]);
  // Secondary halo controls handlers
  const handleToggleSecHalo = (e) => {
    const enabled = e.target.checked;
    setSecHaloEnabled(enabled);
    if (!selectedNode || selectedNode.className !== 'Text') return;
    if (enabled) {
      updateBg2TextForNode(selectedNode);
      // Also enforce primary halo directly above secondary, and main text on top
      try {
        updateBgTextForNode(selectedNode);
      } catch (_) {}
    } else {
      removeBg2TextForNode(selectedNode);
    }
  };
  const handleSecHaloColorChange = (hex) => {
    setSecHaloColor(hex);
    if (secHaloEnabled && selectedNode && selectedNode.className === 'Text') {
      updateBg2TextForNode(selectedNode);
    }
  };
  const handleSecHaloWidthChange = (e) => {
    const v = Number(e.target.value);
    setSecHaloWidth(v);
    if (secHaloEnabled && selectedNode && selectedNode.className === 'Text') {
      updateBg2TextForNode(selectedNode);
    }
  };

  // Background controls handlers
  const handleToggleBackground = (e) => {
    const enabled = e.target.checked;
    setBgEnabled(enabled);
    if (!selectedNode || selectedNode.className !== 'Text') return;
    if (enabled) {
      // Auto choose a contrasting background if current one may hide the text
      try {
        const hex = (typeof selectedNode.fill === 'function' ? selectedNode.fill() : selectedNode.attrs?.fill) || color || '#000000';
        const parseHex = (h) => {
          const v = h.replace('#','');
          const bigint = parseInt(v.length === 3 ? v.split('').map(c=>c+c).join('') : v, 16);
          return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
        };
        const { r, g, b } = parseHex(hex);
        const luminance = (0.299*r + 0.587*g + 0.114*b) / 255;
        // If text is light, pick dark bg; if dark text, pick light bg
        const suggested = luminance > 0.7 ? '#222222' : '#ffffff';
        if (!bgFill || bgFill === '#ffffff' || bgFill === '#000000') {
          setBgFill(suggested);
        }
      } catch(_) {}
      updateBgTextForNode(selectedNode);
      // If secondary halo is enabled, ensure it stays at the bottom
      try {
        if (secHaloEnabled) updateBg2TextForNode(selectedNode);
      } catch (_) {}
    } else {
      removeBgTextForNode(selectedNode);
    }
  };
  const handleBgFillChange = (hex) => {
    setBgFill(hex);
    if (bgEnabled && selectedNode && selectedNode.className === 'Text') {
      updateBgTextForNode(selectedNode);
    }
  };
  const handleBgStrokeChange = (hex) => {
    setBgStroke(hex);
    if (bgEnabled && selectedNode && selectedNode.className === 'Text') {
      updateBgTextForNode(selectedNode);
    }
  };
  const handleBgStrokeWidthChange = (e) => {
    const v = Number(e.target.value);
    setBgStrokeWidth(v);
    if (bgEnabled && selectedNode && selectedNode.className === 'Text') {
      updateBgTextForNode(selectedNode);
    }
  };
  const handleBgPaddingChange = (e) => {
    const v = Number(e.target.value);
    setBgPadding(v);
    if (bgEnabled && selectedNode && selectedNode.className === 'Text') {
      updateBgTextForNode(selectedNode);
    }
  };
  const handleBgCornerRadiusChange = (e) => {
    const v = Number(e.target.value);
    setBgCornerRadius(v);
    if (bgEnabled && selectedNode && selectedNode.className === 'Text') {
      updateBgTextForNode(selectedNode);
    }
  };

  // Reset panel to default state
  const resetPanel = useCallback(() => {
    console.log('TextTab: Resetting panel to default state');
    setSelectedNode(null);
    setText('');
    setFontFamily('');
    setFontWeight('normal');
    setFontSize(32);
    setColor('#ed0101');
    // Reset outline & shadow
    setStrokeColor('#000000');
    setStrokeWidth(0);
    setShadowColor('#000000');
    setShadowBlur(0);
    setShadowOffsetX(0);
    setShadowOffsetY(0);
    setShadowOpacity(0.4);
    // Reset background
    setBgEnabled(false);
    setBgFill('#ffffff');
    setBgStroke('#000000');
    setBgStrokeWidth(1);
    setBgPadding(8);
    setBgCornerRadius(8);
    // Reset secondary halo
    setSecHaloEnabled(false);
    setSecHaloColor('#000000');
    setSecHaloWidth(8);
    // Re-enable auto-add behavior so typing adds a new text node after resets
    if (shouldAddOnChange) {
      shouldAddOnChange.current = true;
    }
  }, []);

  // Handle view/design index changes to reset the text tab
  useEffect(() => {
    const handleViewChange = () => {
      console.log('TextTab: View changed, resetting text tab');
      resetPanel();
    };

    // Support both lowercase and uppercase event variants
    window.addEventListener('viewChanged', handleViewChange);
    window.addEventListener('VIEW_CHANGED', handleViewChange);
    // Reset when design index changes as well
    window.addEventListener('DESIGN_INDEX_CHANGED', handleViewChange);

    // Clean up event listeners
    return () => {
      window.removeEventListener('viewChanged', handleViewChange);
      window.removeEventListener('VIEW_CHANGED', handleViewChange);
      window.removeEventListener('DESIGN_INDEX_CHANGED', handleViewChange);
    };
  }, [resetPanel]);

  // Handle node selection changes
  useEffect(() => {
    if (!konvaController) return;

    const updatePanelFromNode = (node) => {
      try {
        console.log('TextTab: updatePanelFromNode called');
        
        if (!node) {
          console.log('TextTab: No node provided, resetting panel');
          resetPanel();
          return;
        }

        // Safely check if this is a text node using multiple methods
        const isTextNode = 
          (node.className && node.className === 'Text') || 
          (node.getType && typeof node.getType === 'function' && node.getType() === 'Text') || 
          (node.attrs && typeof node.attrs.text !== 'undefined');
        
        console.log('TextTab: Is text node?', isTextNode);
        
        if (isTextNode) {
          // Get text properties safely with fallbacks
          let nodeText = '';
          let nodeFontFamily = '';
          let nodeFontSize = 32;
          let nodeFill = '#ed0101';
          let nodeFontStyle = '';
          let nodeStroke = '#000000';
          let nodeStrokeWidth = 0;
          let nShadowColor = '#000000';
          let nShadowBlur = 0;
          let nShadowOffsetX = 0;
          let nShadowOffsetY = 0;
          let nShadowOpacity = 0.4;
          
          try {
            nodeText = typeof node.text === 'function' ? node.text() : (node.attrs?.text || '');
          } catch (e) {
            console.warn('TextTab: Error getting text property', e);
          }
          
          try {
            nodeFontFamily = typeof node.fontFamily === 'function' ? node.fontFamily() : (node.attrs?.fontFamily || '');
          } catch (e) {
            console.warn('TextTab: Error getting fontFamily property', e);
          }
          
          try {
            nodeFontSize = typeof node.fontSize === 'function' ? node.fontSize() : (node.attrs?.fontSize || 32);
          } catch (e) {
            console.warn('TextTab: Error getting fontSize property', e);
          }
          
          try {
            nodeFill = typeof node.fill === 'function' ? node.fill() : (node.attrs?.fill || '#ed0101');
          } catch (e) {
            console.warn('TextTab: Error getting fill property', e);
          }
          
          try {
            nodeFontStyle = typeof node.fontStyle === 'function' ? node.fontStyle() : (node.attrs?.fontStyle || '');
          } catch (e) {
            console.warn('TextTab: Error getting fontStyle property', e);
          }
          // Outline & Shadow props
          try {
            nodeStroke = typeof node.stroke === 'function' ? (node.stroke() || '#000000') : (node.attrs?.stroke || '#000000');
          } catch (e) {
            console.warn('TextTab: Error getting stroke', e);
          }
          try {
            nodeStrokeWidth = typeof node.strokeWidth === 'function' ? (node.strokeWidth() || 0) : (node.attrs?.strokeWidth || 0);
          } catch (e) {
            console.warn('TextTab: Error getting strokeWidth', e);
          }
          try {
            nShadowColor = typeof node.shadowColor === 'function' ? (node.shadowColor() || '#000000') : (node.attrs?.shadowColor || '#000000');
          } catch (e) {
            console.warn('TextTab: Error getting shadowColor', e);
          }
          try {
            nShadowBlur = typeof node.shadowBlur === 'function' ? (node.shadowBlur() || 0) : (node.attrs?.shadowBlur || 0);
          } catch (e) {
            console.warn('TextTab: Error getting shadowBlur', e);
          }
          try {
            // Prefer functions if available
            if (typeof node.shadowOffsetX === 'function' && typeof node.shadowOffsetY === 'function') {
              nShadowOffsetX = node.shadowOffsetX() || 0;
              nShadowOffsetY = node.shadowOffsetY() || 0;
            } else if (typeof node.shadowOffset === 'function') {
              const off = node.shadowOffset();
              if (off && typeof off.x === 'number' && typeof off.y === 'number') {
                nShadowOffsetX = off.x;
                nShadowOffsetY = off.y;
              }
            } else if (node.attrs?.shadowOffset) {
              const off = node.attrs.shadowOffset;
              nShadowOffsetX = typeof off.x === 'number' ? off.x : 0;
              nShadowOffsetY = typeof off.y === 'number' ? off.y : 0;
            }
          } catch (e) {
            console.warn('TextTab: Error getting shadowOffset', e);
          }
          try {
            nShadowOpacity = typeof node.shadowOpacity === 'function' ? (node.shadowOpacity() ?? 0.4) : (node.attrs?.shadowOpacity ?? 0.4);
          } catch (e) {
            console.warn('TextTab: Error getting shadowOpacity', e);
          }
          
          console.log('TextTab: Text node properties:', {
            text: nodeText,
            fontFamily: nodeFontFamily,
            fontSize: nodeFontSize,
            fill: nodeFill
          });
          
          // Update state with node properties
          setText(nodeText);
          setFontFamily(nodeFontFamily);
          setFontWeight(
            (nodeFontStyle && nodeFontStyle.includes && nodeFontStyle.includes('bold')) || 
            node.attrs?.fontWeight === 'bold' ? 'bold' : 'normal'
          );
          setFontSize(nodeFontSize);
          setColor(nodeFill);
          setStrokeColor(nodeStroke);
          setStrokeWidth(Number(nodeStrokeWidth) || 0);
          setShadowColor(nShadowColor);
          setShadowBlur(Number(nShadowBlur) || 0);
          setShadowOffsetX(Number(nShadowOffsetX) || 0);
          setShadowOffsetY(Number(nShadowOffsetY) || 0);
          setShadowOpacity(Number(nShadowOpacity));
          // Sync halo states from node flags
          const hasBg = !!(node.attrs && (node.attrs.__bgTextId));
          // Force disable and remove primary background halo per Option B
          if (hasBg) {
            try { removeBgTextForNode(node); } catch (_) {}
          }
          setBgEnabled(false);
          const hasSec = !!(node.attrs && node.attrs.__bg2TextId);
          // Always enable secondary halo; create it if missing
          setSecHaloEnabled(true);
          if (!hasSec) {
            try { updateBg2TextForNode(node); } catch (_) {}
          }
          // Restore halo colors/widths from node attrs if present to avoid reset after move
          try {
            if (hasBg) {
              if (typeof node.attrs.__bgHaloColor === 'string') setBgFill(node.attrs.__bgHaloColor);
              if (typeof node.attrs.__bgHaloWidth === 'number') setBgStrokeWidth(node.attrs.__bgHaloWidth);
            }
            if (hasSec) {
              if (typeof node.attrs.__bg2HaloColor === 'string') setSecHaloColor(node.attrs.__bg2HaloColor);
              if (typeof node.attrs.__bg2HaloWidth === 'number') setSecHaloWidth(node.attrs.__bg2HaloWidth);
            }
          } catch (_) {}
          
          console.log('TextTab: Panel updated with node data');
        } else {
          console.warn('TextTab: Not a text node, resetting panel');
          setText('');
          setFontFamily('');
          setFontWeight('normal');
          setFontSize(32);
          setColor('#ed0101');
          setStrokeColor('#000000');
          setStrokeWidth(0);
          setShadowColor('#000000');
          setShadowBlur(0);
          setShadowOffsetX(0);
          setShadowOffsetY(0);
          setShadowOpacity(0.4);
          setBgEnabled(false);
          setSecHaloEnabled(false);
        }
      } catch (error) {
        console.error('TextTab: Error updating panel:', error);
        // Reset to defaults on error
        setText('');
        setFontFamily('');
        setFontWeight('normal');
        setFontSize(32);
        setColor('#ed0101');
        setStrokeColor('#000000');
        setStrokeWidth(0);
        setShadowColor('#000000');
        setShadowBlur(0);
        setShadowOffsetX(0);
        setShadowOffsetY(0);
        setShadowOpacity(0.4);
        setBgEnabled(false);
      }
    };

    // Handle general selection changes from stage events
    const handleSelectionChange = () => {
      try {
        const node = konvaController?.getSelectedNode?.();
        console.log('TextTab: handleSelectionChange - node from controller:', node);
        if (node) {
          setSelectedNode(node);
          updatePanelFromNode(node);
        } else {
          // Non-click scenarios (like drag/transform) can briefly report null; do not reset here
        }
      } catch (error) {
        console.error('TextTab: Error in handleSelectionChange:', error);
      }
    };
    // A non-destructive sync that never resets state on null (used for drag/transform end)
    const syncSelectionNonDestructive = () => {
      try {
        const node = konvaController?.getSelectedNode?.();
        if (node) {
          setSelectedNode(node);
          updatePanelFromNode(node);
        }
      } catch (e) {
        console.warn('TextTab: syncSelectionNonDestructive failed', e);
      }
    };
    
    // Handle stage click on empty area (deselection)
    const handleStageClick = (e) => {
      try {
        // If clicking directly on the stage (not on a node)
        if (e.target === konvaController?.stage) {
          console.log('TextTab: Clicked on empty stage area, resetting panel');
          resetPanel();
        }
      } catch (error) {
        console.error('TextTab: Error in handleStageClick:', error);
      }
    };

    // Handle specific text node selection event
    const handleTextNodeSelected = (e) => {
      try {
        console.log('TextTab: textNodeSelected event received');
        
        // Try to get the node from different possible locations
        let node = e?.detail?.node || window.selectedKonvaNode;
        
        if (node) {
          console.log('TextTab: Setting selected node from textNodeSelected event');
          setSelectedNode(node);
          updatePanelFromNode(node);
        } else {
          console.warn('TextTab: No valid node in textNodeSelected event');
        }
      } catch (error) {
        console.error('TextTab: Error in handleTextNodeSelected:', error);
      }
    };

    // Set up event listeners safely
    try {
      // Listen for selection changes on stage
      const stage = konvaController?.stage;
      if (stage) {
        stage.on('click tap', handleStageClick); // Direct stage click handler
        stage.on('dragend', syncSelectionNonDestructive);
        stage.on('transformend', syncSelectionNonDestructive);
      }

      // Listen for textNodeSelected event on window
      window.addEventListener('textNodeSelected', handleTextNodeSelected);
      
      // Listen for node deselection event
      window.addEventListener('nodeDeselected', resetPanel);

      console.log('TextTab: Event listeners set up');

      // Initialize panel from current selection on tab open (no extra click needed)
      try {
        handleSelectionChange();
      } catch (e) {
        console.warn('TextTab: initial selection sync failed', e);
      }

      return () => {
        try {
          // Clean up event listeners
          if (stage) {
            stage.off('click tap', handleStageClick);
            stage.off('dragend', syncSelectionNonDestructive);
            stage.off('transformend', syncSelectionNonDestructive);
          }
          window.removeEventListener('textNodeSelected', handleTextNodeSelected);
          window.removeEventListener('nodeDeselected', resetPanel);
          console.log('TextTab: Event listeners cleaned up');
        } catch (error) {
          console.error('TextTab: Error cleaning up event listeners:', error);
        }
      };
    } catch (error) {
      console.error('TextTab: Error setting up event listeners:', error);
      return () => {}; // Return empty cleanup function
    }
  }, [konvaController]);

  // This effect is no longer needed as we apply changes immediately in the handlers
  // We'll keep a simplified version just to sync any missed changes
  useEffect(() => {
    try {
      if (!selectedNode) return;
      
      // Safely check if this is a text node
      const isTextNode = 
        (selectedNode.className && selectedNode.className === 'Text') || 
        (selectedNode.getType && typeof selectedNode.getType === 'function' && selectedNode.getType() === 'Text');
      
      if (!isTextNode) return;
      
      // Apply all properties in one batch update
      applyChangesToNode({
        text: text,
        fontFamily: fontFamily,
        fontSize: Number(fontSize),
        fill: color,
        fontStyle: fontWeight === 'bold' ? 'bold' : 'normal'
      });
      
    } catch (error) {
      console.error('TextTab: Error in node update effect:', error);
    }
  }, [selectedNode]); // Only run when selected node changes, not on every property change

  // Live halo syncing during drag/transform so halos always stay behind and follow
  useEffect(() => {
    try {
      if (!selectedNode || selectedNode.className !== 'Text') return;
      const updateHalos = () => {
        try {
          if (bgEnabled) updateBgTextForNode(selectedNode);
          if (secHaloEnabled) updateBg2TextForNode(selectedNode);
        } catch (e) {
          console.warn('TextTab: live halo update failed', e);
        }
      };
      selectedNode.on('dragmove', updateHalos);
      selectedNode.on('transform', updateHalos);
      selectedNode.on('dragend', updateHalos);
      selectedNode.on('transformend', updateHalos);
      return () => {
        try {
          selectedNode.off('dragmove', updateHalos);
          selectedNode.off('transform', updateHalos);
          selectedNode.off('dragend', updateHalos);
          selectedNode.off('transformend', updateHalos);
        } catch (_) {}
      };
    } catch (e) {
      console.warn('TextTab: error wiring live halo listeners', e);
    }
  }, [selectedNode, bgEnabled, secHaloEnabled, bgFill, secHaloColor, secHaloWidth]);


  // Set initial font when CSV fonts are loaded
  useEffect(() => {
    // Only set font if no font is selected and we have CSV fonts
    if (!fontFamily && customFonts.length > 0) {
      const firstCsvFont = customFonts[0].name; // Use first CSV font
      setFontFamily(firstCsvFont);
      document.documentElement.style.setProperty('--selected-font', `"${firstCsvFont}"`);
      console.log('Initial font set to first CSV font:', firstCsvFont);
    }
  }, [fontFamily, customFonts]);
  
  // Preload the first font when component mounts
  useEffect(() => {
    const preloadFirstFont = async () => {
      if (!configData?.FontUrl) return;
      
      try {
        console.log('📝 Preloading first available font...');
        const loadedFonts = await fontService.loadFonts(configData.FontUrl);
        if (loadedFonts && loadedFonts.length > 0) {
          const firstFont = loadedFonts[0].name;
          console.log(`📝 Preloading first font: ${firstFont}`);
          await fontService.loadFontOnDemand(firstFont);
          
          // Update font states
          setCustomFonts(loadedFonts);
          setFontMetadata(loadedFonts);
          setFontFamily(firstFont);
          
          console.log(`📝 Successfully preloaded first font: ${firstFont}`);
        }
      } catch (error) {
        console.warn('Failed to preload first font:', error);
      }
    };
    
    preloadFirstFont();
    
    return () => {
      // Cleanup if needed
    };
  }, [configData?.FontUrl]);

  // Load custom fonts on-demand when user opens font dropdown
  const loadCustomFontsOnDemand = async () => {
    if (!configData?.FontUrl || customFonts.length > 0) {
      return; // Already loaded or no custom fonts available
    }
    
    try {
      console.log('📝 Loading custom fonts on-demand...');
      setLoadingFonts(true);
      
      // Load CSV font metadata without preloading the actual fonts
      const loadedFonts = await fontService.loadFonts(configData.FontUrl);
      setCustomFonts(loadedFonts || []);
      
      // Only use CSV fonts for the dropdown (system fonts are for UI only)
      setFontMetadata(loadedFonts || []);
      
    } catch (error) {
      console.error('Error loading custom fonts:', error);
    } finally {
      setLoadingFonts(false);
    }
  };

  // Apply changes to the selected node
  const applyChangesToNode = (updatedProps) => {
    try {
      if (!selectedNode) return;
      
      console.log('TextTab: Applying changes to node:', updatedProps);
      
      // Track if text was updated
      let textUpdated = false;
      
      // Special handling for text property to ensure it updates properly
      if ('text' in updatedProps) {
        if (typeof selectedNode.text === 'function') {
          selectedNode.text(updatedProps.text);
        } else if (selectedNode.attrs) {
          selectedNode.attrs.text = updatedProps.text;
        }
        // Remove text from updatedProps to avoid duplicate updates
        const { text, ...restProps } = updatedProps;
        updatedProps = restProps;
        textUpdated = true;
      }
      
      // Update remaining node properties
      if (Object.keys(updatedProps).length > 0) {
        if (typeof selectedNode.setAttrs === 'function') {
          selectedNode.setAttrs(updatedProps);
        } else {
          // Fallback to individual property updates
          Object.entries(updatedProps).forEach(([key, value]) => {
            if (typeof selectedNode[key] === 'function') {
              selectedNode[key](value);
            } else if (selectedNode.attrs) {
              selectedNode.attrs[key] = value;
            }
          });
        }
      }
      
      // Force update the layer
      try {
        const layer = selectedNode.getLayer && typeof selectedNode.getLayer === 'function' ? selectedNode.getLayer() : null;
        if (layer) {
          // Ensure the node is marked as dirty for redraw
          selectedNode._clearCache && selectedNode._clearCache();
          
          // If text was updated, update the delete button position
          if (textUpdated && selectedNode.deleteButtonGroup && konvaController?.updateDeleteButtonPosition) {
            konvaController.updateDeleteButtonPosition(selectedNode, selectedNode.deleteButtonGroup, layer);
          }
          
          // If background is enabled, update its geometry to hug the text
          try {
            if (bgEnabled && ((selectedNode.className === 'Text') || (selectedNode.attrs && selectedNode.attrs.text))) {
              updateBgTextForNode(selectedNode);
            }
            if (secHaloEnabled && ((selectedNode.className === 'Text') || (selectedNode.attrs && selectedNode.attrs.text))) {
              updateBg2TextForNode(selectedNode);
            }
          } catch (e) {
            console.warn('TextTab: Failed to update background rect after change', e);
          }

          // Batch draw the layer
          if (typeof layer.batchDraw === 'function') {
            layer.batchDraw();
          } else if (typeof layer.draw === 'function') {
            layer.draw();
          }
          
          // Also try to redraw the stage if available
          const stage = layer.getStage && layer.getStage();
          if (stage && typeof stage.batchDraw === 'function') {
            stage.batchDraw();
          }
        }
      } catch (e) {
        console.warn('TextTab: Error updating layer:', e);
      }
      
      // Log the updated node state for debugging
      console.log('TextTab: Node after update:', {
        text: typeof selectedNode.text === 'function' ? selectedNode.text() : selectedNode.attrs?.text,
        attrs: selectedNode.attrs
      });
      
    } catch (error) {
      console.error('TextTab: Error applying changes to node:', error);
    }
  };

  // --- Background halo helpers (duplicate Text behind) ---
  const getOrCreateBgText = (node) => {
    try {
      if (!node) return null;
      const layer = node.getLayer && node.getLayer();
      const parent = node.getParent && node.getParent();
      if (!layer || !parent) return null;

      const bgId = node.attrs.__bgTextId || `bgtext-${(typeof node.id === 'function' ? node.id() : node.attrs?.id) || Date.now()}`;
      let dup = layer.findOne(`#${bgId}`);
      if (!dup) {
        dup = new Konva.Text({ id: bgId, name: 'text-bg-dup', listening: false });
        parent.add(dup);
        if (typeof dup.insertBefore === 'function') dup.insertBefore(node); else dup.moveToBottom();
        node.attrs.__bgTextId = bgId;
      }
      return dup;
    } catch (e) {
      console.warn('TextTab: getOrCreateBgText failed', e);
      return null;
    }
  };

  const removeBgTextForNode = (node) => {
    try {
      if (!node || !node.attrs?.__bgTextId) return;
      const layer = node.getLayer && node.getLayer();
      if (!layer) return;
      const dup = layer.findOne(`#${node.attrs.__bgTextId}`);
      if (dup) dup.destroy();
      delete node.attrs.__bgTextId;
      layer.batchDraw && layer.batchDraw();
    } catch (e) {
      console.warn('TextTab: removeBgTextForNode failed', e);
    }
  };

  const updateBgTextForNode = (node) => {
    try {
      if (!node) return;
      let dup = getOrCreateBgText(node);
      if (!dup) return;

      // Mirror text attributes
      const attrs = {
        text: (typeof node.text === 'function' ? node.text() : node.attrs?.text) || '',
        fontSize: (typeof node.fontSize === 'function' ? node.fontSize() : node.attrs?.fontSize) || 32,
        fontFamily: (typeof node.fontFamily === 'function' ? node.fontFamily() : node.attrs?.fontFamily) || 'Arial',
        fontStyle: (typeof node.fontStyle === 'function' ? node.fontStyle() : node.attrs?.fontStyle) || 'normal',
        align: (typeof node.align === 'function' ? node.align() : node.attrs?.align) || 'center',
        x: (typeof node.x === 'function' ? node.x() : node.attrs?.x) || 0,
        y: (typeof node.y === 'function' ? node.y() : node.attrs?.y) || 0,
        offsetX: (typeof node.offsetX === 'function' ? node.offsetX() : node.attrs?.offsetX) || 0,
        offsetY: (typeof node.offsetY === 'function' ? node.offsetY() : node.attrs?.offsetY) || 0,
        scaleX: (typeof node.scaleX === 'function' ? node.scaleX() : node.attrs?.scaleX) || 1,
        scaleY: (typeof node.scaleY === 'function' ? node.scaleY() : node.attrs?.scaleY) || 1,
        rotation: (typeof node.rotation === 'function' ? node.rotation() : node.attrs?.rotation) || 0,
        skewX: node.skewX ? node.skewX() : (node.attrs?.skewX || 0),
        skewY: node.skewY ? node.skewY() : (node.attrs?.skewY || 0),
        listening: false,
        fill: 'rgba(0,0,0,0)',
        stroke: null,
        strokeWidth: 0,
        fillAfterStrokeEnabled: false,
      };
      dup.setAttrs(attrs);
      // Persist primary halo settings on the text node
      node.attrs.__bgHaloColor = bgFill;

      // Robust z-order: main text on top, primary halo just below, secondary halo (if any) below that
      const parent = node.getParent && node.getParent();
      if (parent && typeof node.moveToTop === 'function') {
        node.moveToTop();
        const nodeIdx = node.index();
        dup.zIndex(Math.max(nodeIdx - 1, 0));
        const layer = node.getLayer && node.getLayer();
        const secId = node.attrs.__bg2TextId;
        const sec = secId && layer ? layer.findOne(`#${secId}`) : null;
        if (sec) {
          sec.zIndex(Math.max(nodeIdx - 2, 0));
        }
        layer && layer.batchDraw && layer.batchDraw();
      }
    } catch (e) {
      console.warn('TextTab: updateBgTextForNode failed', e);
    }
  };

  // Secondary halo (second duplicate behind primary halo)
  const getOrCreateBg2Text = (node) => {
    try {
      if (!node) return null;
      const layer = node.getLayer && node.getLayer();
      const parent = node.getParent && node.getParent();
      if (!layer || !parent) return null;

      const secId = node.attrs.__bg2TextId || `bg2text-${(typeof node.id === 'function' ? node.id() : node.attrs?.id) || Date.now()}`;
      let sec = layer.findOne(`#${secId}`);
      if (!sec) {
        sec = new Konva.Text({ id: secId, name: 'text-bg2-dup', listening: false });
        parent.add(sec);
        // Position it behind primary halo if exists, otherwise behind main node
        const primaryId = node.attrs.__bgTextId;
        const primary = primaryId ? layer.findOne(`#${primaryId}`) : null;
        if (primary && typeof sec.insertBefore === 'function') {
          sec.insertBefore(primary);
        } else if (typeof sec.insertBefore === 'function') {
          sec.insertBefore(node);
        }
        node.attrs.__bg2TextId = secId;
      }
      return sec;
    } catch (e) {
      console.warn('TextTab: getOrCreateBg2Text failed', e);
      return null;
    }
  };

  const removeBg2TextForNode = (node) => {
    try {
      if (!node || !node.attrs?.__bg2TextId) return;
      const layer = node.getLayer && node.getLayer();
      if (!layer) return;
      const sec = layer.findOne(`#${node.attrs.__bg2TextId}`);
      if (sec) sec.destroy();
      delete node.attrs.__bg2TextId;
      layer.batchDraw && layer.batchDraw();
    } catch (e) {
      console.warn('TextTab: removeBg2TextForNode failed', e);
    }
  };

  const updateBg2TextForNode = (node) => {
    try {
      if (!node) return;
      // Remove secondary halo if present
      const layer = node.getLayer && node.getLayer();
      if (layer && node.attrs?.__bg2TextId) {
        const sec = layer.findOne(`#${node.attrs.__bg2TextId}`);
        if (sec) sec.destroy();
        delete node.attrs.__bg2TextId;
        layer.batchDraw && layer.batchDraw();
      }
    } catch (e) {
      console.warn('TextTab: updateBg2TextForNode cleanup failed', e);
    }
  };

  // Add text to canvas
  const addTextToCanvas = async (textValue) => {
    if (!konvaController?.stage || !textValue.trim()) return;
    
    try {
      const stage = konvaController.stage;
      const target = (konvaController.getTargetCenter && typeof konvaController.getTargetCenter === 'function')
        ? konvaController.getTargetCenter()
        : { x: stage.width() / 2, y: stage.height() / 2 };
      const x = target.x;
      const y = target.y;
      
      // Ensure we have a valid font family
      let effectiveFontFamily = fontFamily;
      
      // If no font is selected, use the first available font or fallback to Arial
      if (!effectiveFontFamily) {
        if (customFonts.length > 0) {
          effectiveFontFamily = customFonts[0].name;
        } else if (fontMetadata.length > 0) {
          effectiveFontFamily = fontMetadata[0].name;
        } else {
          effectiveFontFamily = 'Arial';
        }
        setFontFamily(effectiveFontFamily);
      }
      
      console.log('Adding text with font:', effectiveFontFamily, 'size:', fontSize, 'color:', color);
      
      // Ensure the font is loaded before creating the text node
      if (effectiveFontFamily !== 'Arial' && fontService) {
        try {
          await fontService.loadFontOnDemand(effectiveFontFamily);
        } catch (error) {
          console.warn(`Failed to load font ${effectiveFontFamily}:`, error);
          effectiveFontFamily = 'Arial'; // Fallback to Arial if font loading fails
        }
      }
      
      // Add text using KonvaController
      const textNode = await konvaController.addText(textValue.trim(), x, y, {
        fontFamily: effectiveFontFamily,
        fontWeight,
        fontSize: parseInt(fontSize, 10),
        color
      });
      
      if (textNode) {
        setSelectedNode(textNode);
        // Disable auto-adding for subsequent changes
        shouldAddOnChange.current = false;
        // Apply current outline & shadow defaults to the newly added text
        try {
          const props = {
            stroke: strokeColor,
            strokeWidth: Number(strokeWidth) || 0,
            shadowColor: shadowColor,
            shadowBlur: Number(shadowBlur) || 0,
            shadowOpacity: Number(shadowOpacity) || 0,
            shadowOffset: { x: Number(shadowOffsetX) || 0, y: Number(shadowOffsetY) || 0 },
            shadowOffsetX: Number(shadowOffsetX) || 0,
            shadowOffsetY: Number(shadowOffsetY) || 0,
          };
          // Temporarily set selectedNode to new node so apply works immediately
          const prev = selectedNode;
          setSelectedNode(textNode);
          applyChangesToNode(props);
          // Restore state (setSelectedNode already points to textNode)
        } catch (e) {
          console.warn('TextTab: Failed to apply initial stroke/shadow to new text', e);
        }
        // Do not dismiss the mobile keyboard here to avoid losing focus after first keystroke
        // If background is enabled, create it for the new text
        try {
          if (bgEnabled) {
            updateBgTextForNode(textNode);
          }
        } catch (e) {
          console.warn('TextTab: Failed to create background for new text', e);
        }
      }
      
      return textNode;
    } catch (error) {
      console.error('Error adding text:', error);
      return null;
    }
  };

  // Custom color popover removed per request

  // Handle font family change
  const handleFontFamilyChange = async (e) => {
    const value = e.target.value;
    setFontFamily(value);
    
    // Load the font if needed
    if (value && value !== '' && selectedNode) {
      try {
        setLoadingSpecificFont(true);
        console.log(`📝 Loading font '${value}' before applying...`);
        
        // Use loadFontOnDemand instead of loadFont (which doesn't exist)
        // Wait for font to load before applying changes
        await fontService.loadFontOnDemand(value);
        
        // Get current text and width before font change
        const currentText = selectedNode.text ? selectedNode.text() : (selectedNode.attrs?.text || '');
        const currentWidth = selectedNode.width ? selectedNode.width() : (selectedNode.attrs?.width || 0);
        
        // Apply font change first
        applyChangesToNode({ fontFamily: value });
        
        // Wait a moment for the font to be applied
        await new Promise(resolve => setTimeout(resolve, 50));
        
        // Measure new width with the new font
        const newWidth = selectedNode.width ? selectedNode.width() : (selectedNode.attrs?.width || 0);
        
        // If width changed significantly, adjust font size to maintain similar width
        if (currentWidth > 0 && newWidth > 0 && Math.abs(newWidth - currentWidth) / currentWidth > 0.1) {
          const currentFontSize = selectedNode.fontSize ? selectedNode.fontSize() : (selectedNode.attrs?.fontSize || fontSize);
          const scaleFactor = currentWidth / newWidth;
          const newFontSize = Math.round(currentFontSize * scaleFactor);
          
          console.log(`📏 Adjusting font size from ${currentFontSize} to ${newFontSize} to fit new font width`);
          setFontSize(newFontSize);
          applyChangesToNode({ fontSize: newFontSize });
        }
        
        console.log(`📝 Font '${value}' loaded and applied successfully`);
      } catch (error) {
        console.error(`Error loading font '${value}':`, error);
      } finally {
        setLoadingSpecificFont(false);
      }
    }
  };

  // Handle font weight change
  const handleFontWeightChange = (e) => {
    const value = e.target.value;
    setFontWeight(value);
    
    // Apply change immediately to canvas
    applyChangesToNode({ fontStyle: value === 'bold' ? 'bold' : 'normal' });
  };
  
  // Handle font size change
  const handleFontSizeChange = (e) => {
    const value = e.target.value;
    setFontSize(value);
    
    // Apply change immediately to canvas
    applyChangesToNode({ fontSize: Number(value) });
    if (bgEnabled && selectedNode && selectedNode.className === 'Text') {
      updateBgTextForNode(selectedNode);
    }
    if (secHaloEnabled && selectedNode && selectedNode.className === 'Text') {
      updateBg2TextForNode(selectedNode);
    }
    
    // Update delete button position after font size changes
    if (selectedNode?.deleteButtonGroup) {
      const layer = selectedNode.getLayer && selectedNode.getLayer();
      if (layer && konvaController?.updateDeleteButtonPosition) {
        // Small delay to ensure the node has been updated
        setTimeout(() => {
          konvaController.updateDeleteButtonPosition(selectedNode, selectedNode.deleteButtonGroup, layer);
        }, 10);
      }
    }
  };

  // Handle color change
  const handleColorChange = (e) => {
    const value = e.target.value;
    setColor(value);
    
    // Apply change immediately to canvas
    applyChangesToNode({ fill: value });
    try {
      window.dispatchEvent(new CustomEvent('textColorChanged', { detail: { color: value, node: selectedNode } }));
    } catch (err) {
      console.warn('TextTab: Failed to dispatch textColorChanged event', err);
    }
  };

  // Handle outline (stroke) changes
  const handleStrokeColorDirect = (value) => {
    setStrokeColor(value);
    applyChangesToNode({ stroke: value });
  };
  const handleStrokeWidthChange = (e) => {
    const v = Number(e.target.value);
    setStrokeWidth(v);
    applyChangesToNode({ strokeWidth: v });
  };

  // Handle shadow changes
  const handleShadowColorDirect = (value) => {
    setShadowColor(value);
    applyChangesToNode({ shadowColor: value });
  };
  const handleShadowBlurChange = (e) => {
    const v = Number(e.target.value);
    setShadowBlur(v);
    applyChangesToNode({ shadowBlur: v });
  };
  const handleShadowOffsetXChange = (e) => {
    const v = Number(e.target.value);
    setShadowOffsetX(v);
    applyChangesToNode({ shadowOffsetX: v, shadowOffset: { x: v, y: shadowOffsetY } });
  };
  const handleShadowOffsetYChange = (e) => {
    const v = Number(e.target.value);
    setShadowOffsetY(v);
    applyChangesToNode({ shadowOffsetY: v, shadowOffset: { x: shadowOffsetX, y: v } });
  };
  const handleShadowOpacityChange = (e) => {
    let v = Number(e.target.value);
    if (v < 0) v = 0; if (v > 1) v = 1;
    setShadowOpacity(v);
    applyChangesToNode({ shadowOpacity: v });
  };

  // Handle text change - updates existing text or adds new text
  const handleTextChange = async (e) => {
    const value = e.target.value;
    setText(value);
    
    // If we have a selected text node, update it
    if (selectedNode && selectedNode.className === 'Text') {
      applyChangesToNode({ text: value });
      return;
    }
    
    // If no node is selected and we have text, add new text to canvas
    if (value.trim() && shouldAddOnChange.current) {
      await addTextToCanvas(value);
    }
  };

  // Removed handleAddText: button-based adding is no longer used

  // Get the current fonts to display - only show CSV fonts in dropdown
  // System fonts are used for UI elements only, not for user selection
  const getCurrentFonts = () => {
    if (loadingFonts) {
      return [];
    }
    
    // Only show CSV fonts if available
    if (configData?.FontUrl && customFonts.length > 0) {
      return customFonts.map(font => ({
        name: font.name,
        displayName: font.displayName || font.name, // Use the full display name from CSV
        style: { fontFamily: `"${font.name}", Arial, sans-serif` }
      }));
    }
    
    // If no CSV fonts available, return empty array
    // System fonts are for UI only, not shown in dropdown
    return [];
  };

  const currentFonts = getCurrentFonts();
  const showFontLoading = loadingFonts && configData?.FontUrl;
  const presetColors = [
    '#ed0101', '#faa001', '#fff500', '#009925', '#0172b6',
    '#014f9a', '#670a7c', '#a1017f', '#ee0183', '#ffffff', '#000000'
  ];

  return (
    <div className="text-tab">
      <div className="text-controls">
        <div className="option-group">
          <input
            ref={textInputRef}
            type="text"
            value={text}
            onChange={handleTextChange}
            placeholder={t('text.enterText', 'Enter your text')}
            className="text-input"
          />
        </div>
        
        <div className="text-options">
          <div className="option-group">
            <label>{t('text.fontFamily')}</label>
            {isMobile ? (
              <div className="font-dropdown" ref={fontAnchorRef}>
                <button
                  type="button"
                  className="font-dropdown-trigger"
                  disabled={loadingFonts}
                  onClick={() => {
                    try { loadCustomFontsOnDemand(); } catch {}
                    try {
                      const el = fontAnchorRef.current;
                      if (el && typeof el.getBoundingClientRect === 'function') {
                        const r = el.getBoundingClientRect();
                        const spaceBelow = (window.innerHeight || 0) - r.bottom;
                        setFontMenuOpenUp(spaceBelow < 260);
                      }
                    } catch {}
                    setFontMenuOpen((v) => !v);
                  }}
                >
                  <span className="font-dropdown-trigger-text" style={{ fontFamily: fontFamily ? `"${fontFamily}", Arial, sans-serif` : undefined }}>
                    {fontFamily || t('text.selectFont', 'Select a font...')}
                  </span>
                </button>
                {fontMenuOpen && (
                  <div
                    ref={fontMenuRef}
                    className={`font-dropdown-menu${fontMenuOpenUp ? ' up' : ''}`}
                    role="listbox"
                  >
                    <button
                      type="button"
                      className="font-dropdown-item"
                      onClick={() => {
                        setFontMenuOpen(false);
                        handleFontFamilyPick('');
                      }}
                    >
                      {t('text.selectFont', 'Select a font...')}
                    </button>
                    {loadingFonts && (
                      <div className="font-dropdown-item disabled">Loading fonts...</div>
                    )}
                    {currentFonts.map((font, index) => (
                      <button
                        type="button"
                        key={`${font.name}-${index}`}
                        className={`font-dropdown-item${fontFamily === font.name ? ' selected' : ''}`}
                        style={{ fontFamily: `"${font.name}", Arial, sans-serif` }}
                        onClick={() => {
                          setFontMenuOpen(false);
                          handleFontFamilyPick(font.name);
                        }}
                      >
                        {font.displayName || font.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <select
                value={fontFamily}
                onChange={handleFontFamilyChange}
                onFocus={loadCustomFontsOnDemand}
                className="font-select"
                disabled={loadingFonts}
              >
                <option value="">{t('text.selectFont', 'Select a font...')}</option>
                {loadingFonts && <option value="">Loading fonts...</option>}
                {currentFonts.map((font, index) => (
                  <option
                    key={`${font.name}-${index}`}
                    value={font.name}
                    style={{ fontFamily: font.name }}
                  >
                    {font.displayName || font.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          
          <div className="option-group size-control">
            <label>{t('text.fontSize')}</label>
            <div className="size-input-container">
              <input
                type="range"
                min="8"
                max="144"
                value={fontSize}
                onChange={handleFontSizeChange}
                className="size-slider"
              />
              <input
                type="number"
                min="8"
                max="144"
                value={fontSize}
                onChange={handleFontSizeChange}
                className="size-number"
              />
            </div>
          </div>
          
          <div className="option-group">
            <label>{t('text.fontColor')}</label>
            <div className="color-palette">
              {presetColors.map((colorOption) => (
                <div 
                  key={colorOption}
                  className={`color-option ${color === colorOption ? 'selected' : ''} ${colorOption === '#ffffff' ? 'white-color' : ''}`}
                  style={{ backgroundColor: colorOption }}
                  onClick={() => {
                    setColor(colorOption);
                    applyChangesToNode({ fill: colorOption });
                    try {
                      window.dispatchEvent(new CustomEvent('textColorChanged', { detail: { color: colorOption, node: selectedNode } }));
                    } catch (err) {
                      console.warn('TextTab: Failed to dispatch textColorChanged event', err);
                    }
                  }}
                  title={colorOption}
                />
              ))}
            </div>
            <div className="text-color-picker">
              <HuePicker
                color={color}
                onChange={(c) => {
                  const next = c.hex;
                  setColor(next);
                  applyChangesToNode({ fill: next });
                  try {
                    window.dispatchEvent(new CustomEvent('textColorChanged', { detail: { color: next, node: selectedNode } }));
                  } catch (err) {
                    console.warn('TextTab: Failed to dispatch textColorChanged event', err);
                  }
                }}
                width="100%"
                height="12px"
              />

            </div>
          </div>

          {/* Outline controls */}
          <div className="option-group">
            <label>{t('text.textStroke', 'Text Stroke')}</label>
            <div className="color-palette">
              {presetColors.map((c) => (
                <div
                  key={`stroke-${c}`}
                  className={`color-option ${strokeColor === c ? 'selected' : ''} ${c === '#ffffff' ? 'white-color' : ''}`}
                  style={{ backgroundColor: c }}
                  onClick={() => handleStrokeColorDirect(c)}
                  title={c}
                />
              ))}
            </div>
            <div className="text-color-picker">
              <HuePicker
                color={strokeColor}
                onChange={(c) => handleStrokeColorDirect(c.hex)}
                width="100%"
                height="12px"
              />
            </div>
            <div className="size-input-container" style={{ marginTop: '8px' }}>
              <input
                type="range"
                min="0"
                max="20"
                value={strokeWidth}
                onChange={handleStrokeWidthChange}
                className="size-slider"
              />
              <input
                type="number"
                min="0"
                max="20"
                value={strokeWidth}
                onChange={handleStrokeWidthChange}
                className="size-number"
              />
            </div>
          </div>
          

          
        </div>
      </div>
      
      
      {/* <div className="font-info">
        {loadingSpecificFont ? (
          <span className="loading-indicator">{t('text.loadingFont', 'Loading font...')}</span>
        ) : fontMetadata.length > 0 ? (
          <span>{customFonts.length} of {fontMetadata.length} fonts loaded</span>
        ) : null}
        
        <button 
          className="refresh-btn"
          onClick={loadFontMetadata}
          disabled={loadingFonts}
        >
          {loadingFonts ? t('text.loadingFonts') : t('text.refreshFonts')}
        </button>
      </div> */}
    </div>
  );
};

export default TextTab;
